import React, { useState, Fragment } from "react";
import HoverIconButton from "components/HoverIconButton";
import classnames from "classnames";
import { useFormikContext } from "formik";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import ModalActions from "components/ModalActions";
import Grip from "components/Grip";
import { ROW_MIN_HEIGHT, TOOLTIP_POSITION } from "utils/constants/ui";
import { usePageBuilderContext } from "utils/context";
import Analytics, { REMOVE_PRODUCT_FROM_TABLE } from "utils/analytics";
import Pill from "components/Pill";
import get from "lodash/get";
import startCase from "lodash/startCase";
import { CONFIG_FIELDS } from "utils/constants/form";
import { getFieldConfigPill } from "utils/form";
import { getIconForUID } from "components/FormerEditor/utils";
import { COLORS } from "utils/styles";
import { removeManifestFormField } from "utils/manifest";
import { getFieldRemovalSlides } from "utils/constants/confirm";
import { useFirstComboFromFields } from "utils/hooks/manifest";

/**
 * The draggable summary card for the field - not the actual fields settings
 * Field settings located within FieldShow
 */
const FieldListItem = ({
  index,
  name,
  type,
  title,
  subtitle,
  canRemove,
  draggable,
  canEmphasize,
  onClick,
  snapshot
}) => {
  const pageContext = usePageBuilderContext();

  const tracking = pageContext ? pageContext.tracking : {};
  const [showModal, setShowModal] = useState(false);
  const [hover, setHover] = useState();

  const ctxTitle = title || startCase(type) || "N/A";

  const { values, touched, setValues, setTouched } = useFormikContext();
  const fieldsPath = name;
  const fieldPath = `${fieldsPath}[${index}]`;
  const fieldValues = get(values, fieldPath);

  const combo = useFirstComboFromFields([fieldValues]);

  const containerProps = {
    className: classnames(
      "flex flex-row justify-between items-center ph3 pv2 bh--inset relative overflow-hidden",
      {
        "pointer": draggable,
        "bg-content--secondary": hover,
        "bg-white": !hover
      }
    ),
    onMouseEnter: () => !hover && draggable && setHover(true),
    onMouseLeave: () => hover && draggable && setHover(false)
  };

  let action;
  if (canRemove) {
    action = (
      <HoverIconButton
        type="close"
        tooltip="Remove"
        tooltipPosition={TOOLTIP_POSITION.RIGHT}
        onClick={() => {
          Analytics.track(REMOVE_PRODUCT_FROM_TABLE, {
            ...tracking
          });
          setShowModal(true);
        }}
      />
    );
  }

  const bottomBorderAccent = (
    <div className="ph3">
      <div className="bb hairline-1 w-100"></div>
    </div>
  );

  const isConfigField = CONFIG_FIELDS.indexOf(fieldValues.uid) > -1;
  const configPillProps = isConfigField && getFieldConfigPill(fieldValues);

  const Icon = getIconForUID(fieldValues.uid);

  return (
    <Fragment>
      <div {...containerProps}>
        {draggable && <Grip hover={hover} snapshot={snapshot} />}
        <div className="flex flex-column w-100">
          <div className="flex flex-row w-100" style={ROW_MIN_HEIGHT}>
            <div
              onClick={onClick}
              className="w-100 flex flex-column items-start justify-center"
            >
              <div className="flex flex-row items-center">
                <div
                  style={{ height: 24, width: 24 }}
                  className={
                    "flex items-center justify-center bg-content--secondary br2 icon--boundary flex-shrink-0"
                  }
                >
                  <Icon size={16} color={COLORS.white} />
                  <style jsx>{`
                    .icon--boundary {
                      background-color: ${COLORS.primary};
                    }
                  `}</style>
                </div>

                <div
                  className={classnames(
                    "f6 fw5 w-100 link__underline pointer pl2",
                    {
                      pl3: canEmphasize
                    }
                  )}
                >
                  {ctxTitle}
                </div>
              </div>
            </div>
            <div className="flex items-start">{action}</div>
          </div>
          {configPillProps && (
            <div
              className={classnames("w-100 flex flex-row pt2", {
                "justify-between": isConfigField,
                "justify-end": !isConfigField
              })}
            >
              {configPillProps && <Pill {...configPillProps} />}
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <Modal showModal={showModal} onClose={() => setShowModal(false)}>
          <ModalContent slides={getFieldRemovalSlides(combo)}>
            <ModalActions
              actions={[
                {
                  cta: "Cancel",
                  onClick: () => {
                    setShowModal(false);
                  }
                },
                {
                  cta: "Yes",
                  onClick: () => {
                    removeManifestFormField({
                      values,
                      touched,
                      setValues,
                      setTouched,
                      name: fieldPath
                    });
                  }
                }
              ]}
            />
          </ModalContent>
        </Modal>
      )}
      {bottomBorderAccent}
    </Fragment>
  );
};

export default FieldListItem;
